import { useSyncExternalStore } from "react";

export type OfficialStatementDeclarant = {
    fullName: string;
    address: string;
    contactMethod: string;
    contactNumber: string;
};

export type OfficialStatementQA = {
    question: string;
    answer: string;
};

export type OfficialStatementAttachment = {
    description: string;
    captured: boolean;
    note: string;
};

export type OfficialStatementData = {
    reference: string;
    caseType: string;
    dateOfRecord: string;
    timeOfRecord: string;
    declarant: OfficialStatementDeclarant;
    investigation: OfficialStatementQA[];
    attachments?: OfficialStatementAttachment[];
    declarantName: string;
    officerName: string;
    locked: boolean;
};

export type OfficialStatementUpdateTarget =
    | "statement"
    | "declarant"
    | "investigation"
    | `investigation:${number}`
    | "attachments"
    | "locked";

export type OfficialStatementUpdateEvent = {
    target: OfficialStatementUpdateTarget;
    id: number;
};

export type OfficialStatementSnapshot = {
    statement: OfficialStatementData | null;
    lastUpdate: OfficialStatementUpdateEvent | null;
};

type ToolResult = {
    statement: OfficialStatementData;
    updateTarget?: OfficialStatementUpdateTarget | null;
};

const listeners = new Set<() => void>();

let currentStatement: OfficialStatementData | null = null;
let currentUpdate: OfficialStatementUpdateEvent | null = null;
let currentSnapshot: OfficialStatementSnapshot = {
    statement: currentStatement,
    lastUpdate: currentUpdate,
};
let nextEventId = 1;

export function useOfficialStatement() {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function clearOfficialStatement() {
    if (currentStatement === null && currentUpdate === null) {
        return;
    }

    currentStatement = null;
    currentUpdate = null;
    currentSnapshot = {
        statement: null,
        lastUpdate: null,
    };

    emit();
}

export function applyInvestigatorToolResult(result: unknown) {
    if (!isToolResult(result)) {
        return;
    }

    currentStatement = result.statement;

    if (result.updateTarget) {
        currentUpdate = {
            target: result.updateTarget,
            id: nextEventId++,
        };
    }

    currentSnapshot = {
        statement: currentStatement,
        lastUpdate: currentUpdate,
    };

    emit();
}

function subscribe(listener: () => void) {
    listeners.add(listener);

    return function unsubscribe() {
        listeners.delete(listener);
    };
}

function getSnapshot(): OfficialStatementSnapshot {
    return currentSnapshot;
}

function emit() {
    for (const listener of listeners) {
        listener();
    }
}

function isToolResult(value: unknown): value is ToolResult {
    if (!isRecord(value)) {
        return false;
    }

    return "statement" in value && isOfficialStatementData(value.statement);
}

function isOfficialStatementData(value: unknown): value is OfficialStatementData {
    if (!isRecord(value)) {
        return false;
    }

    return (
        typeof value.reference === "string" &&
        typeof value.caseType === "string" &&
        typeof value.dateOfRecord === "string" &&
        typeof value.timeOfRecord === "string" &&
        isDeclarant(value.declarant) &&
        Array.isArray(value.investigation) &&
        value.investigation.every(isQA) &&
        typeof value.declarantName === "string" &&
        typeof value.officerName === "string" &&
        typeof value.locked === "boolean"
    );
}

function isDeclarant(value: unknown): value is OfficialStatementDeclarant {
    if (!isRecord(value)) {
        return false;
    }

    return (
        typeof value.fullName === "string" &&
        typeof value.address === "string" &&
        typeof value.contactMethod === "string" &&
        typeof value.contactNumber === "string"
    );
}

function isQA(value: unknown): value is OfficialStatementQA {
    if (!isRecord(value)) {
        return false;
    }

    return typeof value.question === "string" && typeof value.answer === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}
